import React from "react";
import "../../../AllComponentCSS.css";
import CodeCopyBlock from "../../../CodeCopyBlock";

const CSS_HowTo = () => {
  return (
    <div className="tutorial-container">
      <h1 className="tutorial-title">⚙️ How To Add CSS</h1>

      <p className="tutorial-intro">
        There are three main ways to apply CSS to your HTML documents:{" "}
        <strong>Inline</strong>, <strong>Internal</strong>, and{" "}
        <strong>External</strong>. Each method has its own use cases and
        advantages.
      </p>

      {/* Inline CSS */}
      <div className="tutorial-section">
        <h2 className="section-title">🎯 1. Inline CSS</h2>
        <p>
          Inline CSS is used to apply a unique style to a single element. The
          style is added directly inside the <code>style</code> attribute of the
          element.
        </p>
        <CodeCopyBlock
          code={`<h1 style="color:blue; text-align:center;">
This is an inline styled heading
</h1>`}
        />
        <div
          style={{
            border: "1px solid #ddd",
            padding: "1rem",
            borderRadius: "8px",
            marginTop: "1rem",
          }}
        >
          <h1 style={{ color: "blue", textAlign: "center" }}>
            This is an inline styled heading
          </h1>
        </div>
      </div>

      {/* Internal CSS */}
      <div className="tutorial-section">
        <h2 className="section-title">📘 2. Internal CSS</h2>
        <p>
          Internal CSS is defined inside a <code>&lt;style&gt;</code> block in
          the <code>&lt;head&gt;</code> section of your HTML document. It is
          useful when you want to style a single page with custom CSS rules.
        </p>
        <CodeCopyBlock
          code={`<head>
  <style>
    h1 {
      color: green;
      text-align: center;
    }
    p {
      font-size: 18px;
      color: gray;
    }
  </style>
</head>
<body>
  <h1>This is a heading</h1>
  <p>This is a paragraph.</p>
</body>`}
        />
        <div
          style={{
            border: "1px solid #ddd",
            padding: "1rem",
            borderRadius: "8px",
            marginTop: "1rem",
          }}
        >
          <h1 style={{ color: "green", textAlign: "center" }}>
            This is a heading
          </h1>
          <p style={{ fontSize: "18px", color: "gray" }}>
            This is a paragraph.
          </p>
        </div>
      </div>

      {/* External CSS */}
      <div className="tutorial-section">
        <h2 className="section-title">🌍 3. External CSS</h2>
        <p>
          External CSS is stored in a separate <code>.css</code> file and linked
          to your HTML using the <code>&lt;link&gt;</code> tag. This is the most
          common and recommended way, as it keeps your styles separate and makes
          them reusable across multiple pages.
        </p>
        <CodeCopyBlock
          code={`<!-- HTML file -->
<head>
  <link rel="stylesheet" href="styles.css">
</head>
<body>
  <h1>This is a heading</h1>
  <p>This is a paragraph.</p>
</body>

/* styles.css */
h1 {
  color: red;
  text-align: center;
}
p {
  font-size: 16px;
  color: black;
}`}
        />

        <div
          style={{
            border: "1px solid #ddd",
            padding: "1rem",
            borderRadius: "8px",
            marginTop: "1rem",
          }}
        >
          <h1 style={{ color: "red", textAlign: "center" }}>
            This is a heading
          </h1>
          <p style={{ fontSize: "16px", color: "black" }}>
            This is a paragraph.
          </p>
        </div>
      </div>

      {/* Comparison Box */}
      <div
        style={{
          background: "linear-gradient(135deg, #eff6ff, #dbeafe)",
          padding: "1rem",
          borderRadius: "12px",
          margin: "2rem 0",
        }}
      >
        <h2 style={{ color: "#1e40af", marginBottom: "0.5rem" }}>
          🔑 Quick Comparison
        </h2>
        <table
          style={{
            width: "100%",
            borderCollapse: "collapse",
            marginTop: "1rem",
          }}
        >
          <thead>
            <tr style={{ background: "#93c5fd" }}>
              <th style={{ padding: "8px", border: "1px solid #2563eb" }}>
                Method
              </th>
              <th style={{ padding: "8px", border: "1px solid #2563eb" }}>
                Use Case
              </th>
              <th style={{ padding: "8px", border: "1px solid #2563eb" }}>
                Pros / Cons
              </th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td style={{ padding: "8px", border: "1px solid #93c5fd" }}>
                Inline
              </td>
              <td style={{ padding: "8px", border: "1px solid #93c5fd" }}>
                Quick fixes or single elements
              </td>
              <td style={{ padding: "8px", border: "1px solid #93c5fd" }}>
                ✅ Easy ❌ Not reusable
              </td>
            </tr>
            <tr>
              <td style={{ padding: "8px", border: "1px solid #93c5fd" }}>
                Internal
              </td>
              <td style={{ padding: "8px", border: "1px solid #93c5fd" }}>
                Styling for one page only
              </td>
              <td style={{ padding: "8px", border: "1px solid #93c5fd" }}>
                ✅ Centralized per page ❌ Not reusable across pages
              </td>
            </tr>
            <tr>
              <td style={{ padding: "8px", border: "1px solid #93c5fd" }}>
                External
              </td>
              <td style={{ padding: "8px", border: "1px solid #93c5fd" }}>
                Large websites with many pages
              </td>
              <td style={{ padding: "8px", border: "1px solid #93c5fd" }}>
                ✅ Reusable & maintainable ❌ Requires extra file
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      {/* Next Steps */}
      <div className="tutorial-section">
        <h2 className="section-title">🚀 Next Steps</h2>
        <p>
          Now that you know <strong>how to add CSS</strong>, continue with{" "}
          <a href="/css/syntax" className="learn-more">
            CSS Syntax
          </a>{" "}
          to learn the rules that make up a CSS stylesheet.
        </p>
      </div>
    </div>
  );
};

export default CSS_HowTo;
